import { TurnInstruction } from '../turnInstructions';
import { ManeuverSign, isTerminal } from './maneuverSign';

/**
 * Lays a route's stored segments end to end and positions every turn along the result.
 *
 * A port of `PlannerInstructionParser` on Android and `GuidanceRoute` on iOS, and it has to
 * stay a faithful one: this decides what the hiker is told, and a route planned here is
 * walked there. Where the three disagree, the web is showing a list of turns nobody will
 * actually hear.
 *
 * Every maneuver carries the address of the stored entry it came from, so the planner can
 * write an edit back to it. The phones gate that behind a flag because their recording builds
 * routes nobody can edit; here there is one caller, so a flag would be dead configuration.
 */

/**
 * Which stored entry a maneuver was read from, so the planner can write back to it.
 *
 * The displayed list is not the stored list — every segment but the first loses its opening
 * entry, terminals are dropped, neighbours collapse and seams are synthesised — so a row's
 * position says nothing about where its entry lives. This is the address that does.
 */
export interface ManeuverOrigin {
  /** The line string the entry is stored on, by its index in the route. */
  segmentIndex: number;
  /** Its index in that line string's own instruction array. */
  entryIndex: number;
}

/** One snapped segment of a planned route, in walking order. */
export interface Segment {
  position: number;
  lengthMeters: number;
  instructions: TurnInstruction[];
  /** "line", "hike", "bike" or "car"; undefined for anything stored before it was captured. */
  profile?: string;
}

/** A turn positioned along the whole route rather than along its own segment. */
export interface RouteManeuver {
  distanceFromStartMeters: number;
  sign: number;
  text: string;
  streetName?: string;
  /** Set when a person added this entry themselves — `"turn"` or `"note"`. */
  cue?: string;
  /** True when [text] is a person's own wording, so nothing may recompose it. */
  isUserWorded: boolean;
  /** Suppressed in the planner. Carried this far so the seam detector can see it. */
  isHidden: boolean;
  /** Set on the entry standing in for a segment drawn as a straight line: that segment's length. */
  straightLineMeters?: number;
  /**
   * Where this was read from. Absent on anything the runtime invented — a seam, the closing
   * arrival — which is exactly what makes those rows uneditable without a second rule.
   */
  origin?: ManeuverOrigin;
}

/** Two turns closer than this along the route are the same turn seen twice. */
const DEDUPE_DISTANCE_METERS = 15;

/**
 * @param geometryTotalMeters the route's length as the map measures it. Given, each segment's
 * turns are scaled onto that segment's share of it. Omitted, the stored distances are used
 * as-is, which is what routes had before the two scales were reconciled.
 */
export function parseInstructions(
  segments: Segment[],
  geometryTotalMeters = 0
): RouteManeuver[] {
  if (segments.length === 0) return [];

  const ordered = [...segments].sort((a, b) => a.position - b.position);
  const lengths = segmentLengths(ordered, geometryTotalMeters);
  const raw: RouteManeuver[] = [];
  let offsetMeters = 0;

  ordered.forEach((segment, index) => {
    const turns = segment.instructions ?? [];
    const isFirst = index === 0;
    const isLast = index === ordered.length - 1;
    const captured = measuredLength(turns) ?? segment.lengthMeters;
    const scale = captured > 0 ? lengths[index] / captured : 1;

    turns.forEach((turn, turnIndex) => {
      // Every segment opens with a "continue" and closes with an arrival, because
      // GraphHopper was asked to route between two waypoints. Only the route's own opening
      // and ending are real; the rest mark waypoints, not decisions.
      if (turnIndex === 0 && !isFirst) return;
      if (isTerminal(turn.sign) && !(isLast && turnIndex === turns.length - 1)) return;

      // A person's sign wins over GraphHopper's, and their wording over its sentence.
      // `text` and `sign` are left untouched in storage, so this is the only place an
      // override applies and clearing the key restores the original.
      //
      // When the direction has been changed the street no longer applies: GraphHopper named
      // the road the *old* turn led onto, and showing it would name the wrong road.
      const keepsStreet = turn.usign === undefined || turn.usign === turn.sign;

      raw.push({
        distanceFromStartMeters: offsetMeters + turn.d * scale,
        sign: turn.usign ?? turn.sign,
        text: turn.utext ?? turn.text,
        streetName: keepsStreet ? turn.street : undefined,
        cue: turn.cue,
        isUserWorded: turn.utext !== undefined || (turn.cue !== undefined && turn.text !== ''),
        isHidden: turn.hidden === true,
        // The segment's own position, not its place in this loop: a route can hold segments
        // left out of the parse, and the address has to survive that. `turnIndex` is the
        // stored index because this iterates the stored array directly.
        origin: { segmentIndex: segment.position, entryIndex: turnIndex }
      });
    });

    offsetMeters += lengths[index];
  });

  return collapseNeighbours(
    raw.sort((a, b) => a.distanceFromStartMeters - b.distanceFromStartMeters)
  );
}

/**
 * Where the segments meet, measured along the whole route.
 *
 * A turn at one of these is invisible in the instructions — see `seamManeuverDetector`. The
 * route's own start and end are not seams, so they are excluded.
 */
export function seamOffsets(segments: Segment[], geometryTotalMeters = 0): number[] {
  if (segments.length < 2) return [];

  const ordered = [...segments].sort((a, b) => a.position - b.position);
  const lengths = segmentLengths(ordered, geometryTotalMeters);
  const seams: number[] = [];
  let offsetMeters = 0;

  for (let i = 0; i < ordered.length - 1; i += 1) {
    offsetMeters += lengths[i];
    seams.push(offsetMeters);
  }
  return seams;
}

/**
 * How long each segment is on the scale the route is measured in.
 *
 * A route does not have one scale. Segments captured by the web planner match their geometry
 * exactly; segments captured by the phones run up to 5% long — and a route can hold both,
 * because editing a hike re-snaps some segments and carries the rest through untouched.
 * Correcting such a route with a single route-wide ratio shrinks the segments that were
 * already right in order to fix the one that wasn't, moving their turns *earlier* than the
 * junctions they describe.
 */
function segmentLengths(ordered: Segment[], geometryTotalMeters: number): number[] {
  const storedTotal = ordered.reduce((sum, s) => sum + s.lengthMeters, 0);
  if (geometryTotalMeters <= 0 || storedTotal <= 0) {
    return ordered.map((s) => measuredLength(s.instructions ?? []) ?? s.lengthMeters);
  }
  return ordered.map((s) => (s.lengthMeters / storedTotal) * geometryTotalMeters);
}

/**
 * How long a segment is, measured the way the turns on it were measured.
 *
 * The closing instruction sits exactly at the segment's end, so it *is* that length. Matched
 * on the terminal sign rather than taken as the last entry, so a segment whose arrival never
 * made it falls back instead of mistaking an ordinary turn for the end.
 */
function measuredLength(turns: TurnInstruction[]): number | undefined {
  for (let i = turns.length - 1; i >= 0; i -= 1) {
    if (isTerminal(turns[i].sign)) return turns[i].d;
  }
  return undefined;
}

/** Entries the dedupe must leave alone — anything a person wrote, reworded or hid. */
export function survivesCollapse(m: RouteManeuver): boolean {
  return m.cue !== undefined || m.isUserWorded || m.isHidden;
}

/**
 * Collapse turns that land on top of each other — a segment join sees both sides.
 *
 * Anything a person authored is never collapsed and never collapses anything: they add a cue
 * *at* a junction, which is precisely where a routed turn already is, so the rule that keeps
 * the list readable would delete the one entry nobody can regenerate.
 *
 * A suppressed entry is exempt for a sharper reason. It sits at a segment join to tell the
 * seam detector the join is already accounted for, and is filtered out afterwards — so if it
 * could collapse a neighbour, deleting one turn would silently delete the real turn beside it
 * and leave the junction with nothing at all.
 */
function collapseNeighbours(maneuvers: RouteManeuver[]): RouteManeuver[] {
  const result: RouteManeuver[] = [];
  maneuvers.forEach((maneuver) => {
    const previous = result[result.length - 1];
    const isDuplicate =
      previous !== undefined &&
      !survivesCollapse(previous) &&
      !survivesCollapse(maneuver) &&
      maneuver.distanceFromStartMeters - previous.distanceFromStartMeters < DEDUPE_DISTANCE_METERS;

    if (isDuplicate && !isTerminal(maneuver.sign)) return;
    if (isDuplicate) result.pop();
    result.push(maneuver);
  });
  return result;
}
